import { Component, ElementRef, Input, ViewChild, OnDestroy } from '@angular/core';
import * as echarts from 'echarts';

@Component({
    selector: 'app-echart-semi-circle',
    templateUrl: './echart-semi-circle.component.html',
    styleUrl: './echart-semi-circle.component.scss',
    standalone: false
})
export class EchartSemiCircleComponent implements OnDestroy {
  @ViewChild('chart', { static: true }) chartElement!: ElementRef;
  chartOption: any;
  @Input() colors: any;
  @Input() data: number[];
  @Input() type: string;
  @Input() title: string;
  windowWidth: number;

  private chartInstance: echarts.ECharts | null = null;
  private resizeHandler = () => this.onResize();

  ngAfterViewInit(): void {
    this.initChart();
    window.addEventListener('resize', this.resizeHandler);
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.resizeHandler);
    if (this.chartInstance) {
      this.chartInstance.dispose();
    }
  }

  private initChart(): void {
    const chartDom = this.chartElement.nativeElement;
    this.chartInstance = echarts.init(chartDom);
    this.setChartOption();
  }

  private setChartOption(): void {
    if (!this.chartInstance) return;
    if (this.type === 'quality') {
      this.chartInstance.setOption(this.getQualityOption());
    } else {
      this.chartInstance.setOption(this.getImpactOption());
    }
  }

  private onResize(): void {
    this.windowWidth = window.outerWidth;
    console.log(this.windowWidth);
    if (this.chartInstance) {
      this.chartInstance.resize();
    }
  }

  private getQualityOption(): any {
    this.chartOption = {
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          return `${params.marker} ${params.name}: ${params.value} (${params.percent}%)`;
        }
      },
      series: [
        // Outer pie for Base Case
        {
          name: 'Base Case',
          type: 'pie',
          radius: ['70%', '90%'],
          center: ['50%', '85%'],
          startAngle: 180,
          endAngle: 360,
          color: ['#6BF3F9'],
          label: {
            color: '#000000',
            fontSize: 12,
            fontWeight: 'bold'
          },
          data: [
            { value: this.data[0], name: 'Base Case', label: { rich: { a: {} } } }
          ],
          z: 2
        },
        // Inner pie for Design/Impact
        {
          name: 'Access From',
          type: 'pie',
          radius: ['50%', '70%'],
          center: ['50%', '85%'],
          startAngle: 180,
          endAngle: 360,
          label: {
            color: '#000000',
            fontSize: 12,
            fontWeight: 'bold'
          },
          color: ['#7DDA58', '#338c1c'],
          data: [
            { value: this.data[1], name: 'Design Case', percentage: this.data[2] },
            { value: this.data[3], name: 'Impact Reduction', percentage: this.data[4] },
          ],
          z: 3
        },
        // Optional: Gauge for annotation
        {
          type: 'gauge',
          startAngle: 180,
          endAngle: 0,
          radius: '65%',
          center: ['50%', '85%'],
          min: 0,
          splitNumber: 1,
          max: this.data[2] + this.data[4],
          axisLine: {
            lineStyle: {
              width: 0
            }
          },
          axisTick: {
            show: false
          },
          splitLine: {
            show: false
          },
          axisLabel: {
            show: false
          },
          pointer: {
            length: '90%',
            width: 4,
            itemStyle: {
              color: '#000'
            }
          },
          detail: {
            show: false // <-- This hides the meter value in the center
          },
          data: [
            {
              value: this.data[2],
              name: ''
            }
          ],
          z: 1
        }
      ]
    };
    return {
      ...this.chartOption
    };
  }

  private getImpactOption(): any {
    // ... (your existing chartOption for quality)
    // Copy the chartOption from your createChart() method here
    // Replace all `this.data[...]` with the same
    // Return the option object
    this.chartOption = {
      title: {
        text: this.title,
        left: 'center',
        top: '2%',
        textStyle: {
          fontSize: this.windowWidth > 1250 ? 18 : 14,
          fontWeight: 'bold'
        }
      },
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          return `${params.marker} ${params.name}: ${params.value} (${params.percent}%)`;
        }
      },
      // legend: {
      //   top: '10%',
      //   left: 'center'
      // },
      series: [
        // Inner pie for Design/Impact
        {
          name: 'Access From',
          type: 'pie',
          radius: ['50%', '70%'], // reduced gap between pies
          center: ['50%', '85%'],
          startAngle: 180,
          endAngle: 360,
          label: {
            show: true,
            formatter: (params: any) => {
              const dataValue = this.data[1];
              console.log(dataValue);
              const iconType = params.name === 'Total Points' ? 'iconHappiest' : dataValue >= 75 ? 'iconHappiest' : dataValue >= 50 ? 'iconHappy' : dataValue >= 25 ? 'iconAvg' : 'iconSad';
              return `{${iconType}|}\n${params.name}\n${params.value} (${params.percent}%)`;
            },
            fontWeight: 'bold', // <-- Add this line to bold the label
            rich: {
              iconHappiest: {
                height: 40,
                width: 40,
                backgroundColor: {
                  image: 'assets/images/man_happiest.png'
                },
                borderRadius: 12.5,
                align: 'center',
                verticalAlign: 'middle',
              }, iconHappy: {
                height: 40,
                width: 40,
                backgroundColor: {
                  image: 'assets/images/man_happy.png'
                },
                borderRadius: 12.5,
                align: 'center',
                verticalAlign: 'middle',
              }, iconAvg: {
                height: 40,
                width: 40,
                backgroundColor: {
                  image: 'assets/images/man_avg.png'
                },
                borderRadius: 12.5,
                align: 'center',
                verticalAlign: 'middle',
              }, iconSad: {
                height: 40,
                width: 40,
                backgroundColor: {
                  image: 'assets/images/man_sad.png'
                },
                borderRadius: 12.5,
                align: 'center',
                verticalAlign: 'middle',
              }
            },
          },
          color: ['#7DDA58', '#338c1c'],
          data: [
            { value: this.data[0], name: 'Achieved Points', percentage: this.data[1] },
            { value: this.data[2], name: 'Total Points', percentage: this.data[3] },
          ],
          z: 3
        }
      ]
    };

    return {
      // ... your chartOption code for quality
      // (copy from your createChart() method)
      ...this.chartOption
    };
  }
}
